import {
  mountAdminClient,
  AdminLoadingPage,
  AdminPage,
  createAdminClientApp,
  getAdminClientMountTarget,
  parseAdminBrowserManifestElement,
  parseAdminBrowserSessionElement,
  createAdminBrowserRegistry,
  installAdminSpaNavigation,
  matchAdminBrowserScreen,
  type AdminBrowserScreen,
  type AdminClientCreateRoot,
  type AdminClientDocumentLike,
  type AdminClientRoot,
  type AdminClientScreenRendererInput,
} from "@zzk/admin";
import {
  ForbiddenPage,
  MaintenancePage,
  NotFoundPage,
  OfflinePage,
  ServerErrorPage,
} from "@zzk/admin-pages";
import {
  ThemeProvider,
  ThemeToggle,
  type ZzkThemeMode,
} from "@zzk/ui";
import { toUtcIsoDateTime } from "@zzk/core";
import { createElement } from "react";
import { createRoot as createReactRoot } from "react-dom/client";
import { brandConfig } from "./brand/index.js";
import { createOperationalOverviewView } from "./modules/observability/ui/overview.js";
import type { OperationalOverview } from "./modules/observability/service.js";
import { createSettingsShellView } from "./modules/settings/ui/settings-shell.js";
import { renderAuthPublicScreen } from "./modules/magic-link-auth/ui/index.js";
import { createOverviewView } from "./modules/overview/ui/index.js";
import { AdminErrorBoundary, buildErrorReporter } from "./error-boundary.js";
import { installEnvelopeDropTelemetry } from "./envelope-telemetry.js";
import { OfflineDetector } from "./offline-detector.js";
import { MaintenanceGate } from "./maintenance-gate.js";

export const generatedAdminClientScriptPath = "/assets/admin-client.js";

export function renderGeneratedAdminClientScreen({
  screen,
}: AdminClientScreenRendererInput) {
  if (screen.id === "overview") {
    return createOverviewView();
  }

  if (screen.id === "observability") {
    return createOperationalOverviewView(emptyOperationalOverview());
  }

  if (screen.id === "settings") {
    return createSettingsShellView({
      userId: "user-reference",
      displayName: "Usuário autenticado",
      locale: "pt-BR",
      updatedAt: "2026-06-14T00:00:00.000Z",
    });
  }

  if (screen.id === "errors.not-found") {
    return NotFoundPage({ homeHref: "/app/overview" });
  }

  if (screen.id === "errors.forbidden") {
    return ForbiddenPage();
  }

  if (screen.id === "errors.server-error") {
    return ServerErrorPage({ retryHref: "/app/overview" });
  }

  if (screen.id === "errors.maintenance") {
    return MaintenancePage();
  }

  if (screen.id === "errors.offline") {
    return OfflinePage({ retryHref: "/app/overview" });
  }

  return AdminPage({
    title: screen.label,
    description:
      "Client runtime mounted. Data is loaded through protected API routes.",
    children: AdminLoadingPage(),
  });
}

const defaultTurnstileSiteKey = "1x00000000000000000000AA";

function isAuthBrowserScreen(screenId: string): boolean {
  return screenId.startsWith("magic-link-auth.") || screenId.startsWith("auth.");
}

export function mountGeneratedAdminClient(
  input: {
    readonly document?: AdminClientDocumentLike;
    readonly createRoot?: AdminClientCreateRoot;
  } = {},
): AdminClientRoot {
  const documentRef =
    input.document ??
    (globalThis as { document?: AdminClientDocumentLike }).document;

  if (!documentRef) {
    return mountAdminClient({
      document: input.document,
      createRoot: input.createRoot,
      shell: { title: brandConfig.name, userLabel: "Usuário autenticado" },
      renderScreen: renderGeneratedAdminClientScreen,
    });
  }

  const target = getAdminClientMountTarget({ document: documentRef });
  const manifest = parseAdminBrowserManifestElement(target.manifestElement);
  const documentSession = parseAdminBrowserSessionElement(
    target.sessionElement,
  );
  const registry = createAdminBrowserRegistry({
    pathPrefix: manifest.pathPrefix,
    defaultPath: manifest.defaultPath,
    screens: manifest.screens,
  });

  const createRoot =
    input.createRoot ??
    ((root) => createReactRoot(root as Parameters<typeof createReactRoot>[0]));
  const root = createRoot(target.root);

  const resolvedDefaultMode = readMetaTagThemeMode();
  const resolvedTurnstileSiteKey =
    readMetaTagContent("zzk-turnstile-site-key") ?? defaultTurnstileSiteKey;

  const renderPath = (activePath: string | null) => {
    const activeScreen = activePath
      ? matchAdminBrowserScreen(registry, activePath)
      : null;
    const content =
      activeScreen && isAuthBrowserScreen(activeScreen.id)
        ? createElement(
            AdminErrorBoundary,
            null,
            renderAuthPublicScreen(
              activeScreen.id,
              resolvedTurnstileSiteKey,
            ) ?? createElement("div", null),
          )
        : createElement(
            MaintenanceGate,
            null,
            createElement(
              OfflineDetector,
              null,
              createElement(
                AdminErrorBoundary,
                null,
                createAdminClientApp({
                  manifest,
                  activePath,
                  shell: {
                    title: brandConfig.name,
                    userLabel: "Usuário autenticado",
                    session: documentSession,
                    themeToggle: createElement(ThemeToggle, null),
                    menuFilter: createAdminSidebarMenuFilter(
                      readSidebarMenuFilterOptions(),
                    ),
                  },
                  renderScreen: renderGeneratedAdminClientScreen,
                }),
              ),
            ),
          );
    root.render(
      createElement(
        ThemeProvider,
        { initialMode: resolvedDefaultMode },
        content,
      ),
    );
  };

  renderPath(target.activePath);

  const cleanup = tryInstallGeneratedAdminSpaNavigation({
    document: documentRef,
    registry,
    onNavigate: renderPath,
  });

  if (!cleanup) return root;

  return {
    render: root.render,
    unmount: () => {
      cleanup();
      root.unmount();
    },
  };
}

export function autoMountGeneratedAdminClient(
  input: {
    readonly document?: AdminClientDocumentLike;
    readonly createRoot?: AdminClientCreateRoot;
  } = {},
): AdminClientRoot | null {
  const documentRef =
    input.document ??
    (globalThis as { document?: AdminClientDocumentLike }).document;
  if (!documentRef) return null;
  installEnvelopeDropTelemetry(buildErrorReporter());
  return mountGeneratedAdminClient({
    document: documentRef,
    createRoot: input.createRoot,
  });
}

function tryInstallGeneratedAdminSpaNavigation(input: {
  readonly document: AdminClientDocumentLike;
  readonly registry: ReturnType<typeof createAdminBrowserRegistry>;
  readonly onNavigate: (activePath: string) => void;
}): (() => void) | null {
  const documentRef = readSpaNavigationDocument(input.document);
  const windowRef = readSpaNavigationWindow();
  if (!documentRef || !windowRef) return null;

  return installAdminSpaNavigation({
    document: documentRef,
    window: windowRef,
    pathPrefix: input.registry.pathPrefix,
    onNavigate: input.onNavigate,
    shouldHandle: (path) =>
      Boolean(matchAdminBrowserScreen(input.registry, path)),
  });
}

function readSpaNavigationDocument(
  documentRef: AdminClientDocumentLike,
): Parameters<typeof installAdminSpaNavigation>[0]["document"] | null {
  if (
    "addEventListener" in documentRef &&
    "removeEventListener" in documentRef &&
    typeof documentRef.addEventListener === "function" &&
    typeof documentRef.removeEventListener === "function"
  ) {
    return documentRef as unknown as Parameters<
      typeof installAdminSpaNavigation
    >[0]["document"];
  }
  return null;
}

function readSpaNavigationWindow():
  | Parameters<typeof installAdminSpaNavigation>[0]["window"]
  | null {
  const candidate = (globalThis as { window?: unknown }).window;
  if (!isRecord(candidate)) return null;
  if (!isRecord(candidate.location)) return null;
  if (!isRecord(candidate.history)) return null;
  if (
    typeof candidate.addEventListener !== "function" ||
    typeof candidate.removeEventListener !== "function" ||
    typeof candidate.history.pushState !== "function" ||
    typeof candidate.location.origin !== "string" ||
    typeof candidate.location.pathname !== "string"
  ) {
    return null;
  }
  return candidate as unknown as Parameters<
    typeof installAdminSpaNavigation
  >[0]["window"];
}

const SIDEBAR_HIDDEN_ERROR_BROWSER_PATHS: ReadonlySet<string> = new Set([
  "/app/404",
  "/app/403",
  "/app/500",
  "/app/maintenance",
  "/app/offline",
]);

const SIDEBAR_HIDDEN_AUTH_BROWSER_PATHS: ReadonlySet<string> = new Set([
  "/app/login",
  "/app/login/code",
  "/app/logout",
]);

const SIDEBAR_HIDDEN_SCREEN_IDS: ReadonlySet<string> = new Set([
  "auth.logout",
  "magic-link-auth",
  "brand",
  "settings",
  "observability",
]);

export interface AdminSidebarMenuFilterOptions {
  readonly mode: string | undefined;
  readonly showDemoRoutes: boolean;
}

export function createAdminSidebarMenuFilter(
  options: AdminSidebarMenuFilterOptions,
): (screen: AdminBrowserScreen) => boolean {
  return (screen) => {
    if (SIDEBAR_HIDDEN_SCREEN_IDS.has(screen.id)) return false;
    if (screen.id.startsWith("auth.")) return false;
    if (screen.id.startsWith("magic-link-auth.")) return false;
    if (SIDEBAR_HIDDEN_AUTH_BROWSER_PATHS.has(screen.browserPath)) return false;
    if (SIDEBAR_HIDDEN_ERROR_BROWSER_PATHS.has(screen.browserPath))
      return options.showDemoRoutes;
    return true;
  };
}

export function readSidebarMenuFilterOptions(): AdminSidebarMenuFilterOptions {
  const env = readImportMetaEnv();
  const rawMode = env?.MODE;
  const mode = typeof rawMode === "string" ? rawMode : undefined;
  const envShowDemo =
    env?.ZZK_SHOW_DEMO_ROUTES === "true" || env?.ZZK_SHOW_DEMO_ROUTES === true;
  const processShowDemo = readProcessShowDemoRoutes();
  return {
    mode,
    showDemoRoutes: Boolean(envShowDemo || processShowDemo),
  };
}

function readImportMetaEnv(): Record<string, unknown> | undefined {
  const meta = import.meta as unknown as { readonly env?: Record<string, unknown> };
  return meta.env;
}

function readProcessShowDemoRoutes(): boolean {
  const proc = (
    globalThis as {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process;
  return proc?.env?.ZZK_SHOW_DEMO_ROUTES === "true";
}

function readMetaTagThemeMode(): ZzkThemeMode | undefined {
  const content = readMetaTagContent("zzk-theme-mode");
  if (content === "light" || content === "dark" || content === "system")
    return content;
  return undefined;
}

function readMetaTagContent(name: string): string | undefined {
  if (typeof globalThis === "undefined") return undefined;
  const doc = (
    globalThis as {
      document?: {
        querySelector?: (
          sel: string,
        ) => { getAttribute?: (name: string) => string | null } | null;
      };
    }
  ).document;
  if (!doc?.querySelector) return undefined;
  const content = doc
    .querySelector(`meta[name="${name}"]`)
    ?.getAttribute?.("content");
  return content && content.length > 0 ? content : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function emptyOperationalOverview(): OperationalOverview {
  return {
    app: "zzk-lens",
    environment: "development",
    generatedAt: toUtcIsoDateTime(0),
    status: "healthy",
    totals: {
      clientErrors: 0,
      fatalClientErrors: 0,
      operationalSignals: 0,
      blockedSignals: 0,
      degradedSignals: 0,
      queueSignals: 0,
      documentSignals: 0,
      moduleSignals: 0,
    },
    categories: [],
    recentSignals: [],
    recentClientErrors: [],
    operatorHints: [],
  };
}

autoMountGeneratedAdminClient();
